import type {
  ProductComparisonRow,
  ProductSellingModeRow,
  ProductWorkflowStep
} from "../types";

export const PRODUCT_COMPARISON_ROWS: ProductComparisonRow[] = [
  {
    id: "represents",
    aspectKey: "products-items-page-table-aspect-represents",
    aspectFallback: "What it represents",
    productKey: "products-items-page-table-product-represents",
    productFallback: "The catalog listing for what you sell",
    itemKey: "products-items-page-table-item-represents",
    itemFallback: "The actual stock available in a store"
  },
  {
    id: "examples",
    aspectKey: "products-items-page-table-aspect-examples",
    aspectFallback: "Examples",
    productKey: "products-items-page-table-product-examples",
    productFallback: '"Bottled water 500ml", "Men\'s cotton shirt"',
    itemKey: "products-items-page-table-item-examples",
    itemFallback:
      "A 500ml water batch with 80 units left, or Size L / Blue with 12 units available"
  },
  {
    id: "details",
    aspectKey: "products-items-page-table-aspect-details",
    aspectFallback: "Typical details",
    productKey: "products-items-page-table-product-details",
    productFallback: "Name, category, brand, image, and selling method",
    itemKey: "products-items-page-table-item-details",
    itemFallback: "Variation, SKU, price, quantities, and stock status"
  },
  {
    id: "quantities",
    aspectKey: "products-items-page-table-aspect-quantities",
    aspectFallback: "Stock quantities",
    productKey: "products-items-page-table-product-quantities",
    productFallback: "No. The product itself does not hold stock counts.",
    itemKey: "products-items-page-table-item-quantities",
    itemFallback:
      "Yes. It shows what is available, reserved, sold, or adjusted."
  },
  {
    id: "store",
    aspectKey: "products-items-page-table-aspect-store",
    aspectFallback: "Store scope",
    productKey: "products-items-page-table-product-store",
    productFallback: "Listed in your store catalog",
    itemKey: "products-items-page-table-item-store",
    itemFallback: "Tracked per store so each location has accurate stock"
  },
  {
    id: "orders",
    aspectKey: "products-items-page-table-aspect-orders",
    aspectFallback: "Sales & purchases",
    productKey: "products-items-page-table-product-orders",
    productFallback: "Defines what staff can search for or select",
    itemKey: "products-items-page-table-item-orders",
    itemFallback:
      "The item quantity is reserved, received, or reduced when orders are processed"
  }
];

export const PRODUCT_WORKFLOW_STEPS: ProductWorkflowStep[] = [
  {
    id: "categories",
    stepKey: "products-items-page-workflow-1",
    stepFallback:
      "Create categories first. Every product must belong to a category, so set these up before adding products."
  },
  {
    id: "products",
    stepKey: "products-items-page-workflow-2",
    stepFallback:
      "Create products. Add one record for each item you sell, assign it to a category, and optionally link it to a supplier."
  },
  {
    id: "items",
    stepKey: "products-items-page-workflow-3",
    stepFallback:
      "Add product items to record stock in each store. Use product items to track size, batch, SKU, variation, quantity, and price."
  },
  {
    id: "qr-print",
    stepKey: "products-items-page-workflow-4",
    stepFallback:
      "Print QR codes for product items after adding stock, or from the product item list, then label shelves, boxes, or batches."
  },
  {
    id: "orders-scan",
    stepKey: "products-items-page-workflow-5",
    stepFallback:
      "When creating a sales or purchase order, staff can scan QR codes to add the correct item quickly instead of searching manually."
  },
  {
    id: "orders-complete",
    stepKey: "products-items-page-workflow-6",
    stepFallback:
      "Complete orders. Kivunova FMS updates reserved, sold, and available quantities on the related product items."
  }
];

export const PRODUCT_SELLING_MODE_ROWS: ProductSellingModeRow[] = [
  {
    id: "single-item",
    modeKey: "products-items-page-selling-mode-single-item",
    modeFallback: "Single item",
    howKey: "products-items-page-selling-mode-single-item-how",
    howFallback: "Sold as one complete item. Each sale quantity is 1.",
    baseUnitKey: "products-items-page-selling-mode-single-item-base-unit",
    baseUnitFallback: "Unit",
    partialKey: "products-items-page-selling-mode-single-item-partial",
    partialFallback: "Not applicable. The item is always sold whole."
  },
  {
    id: "unit-based",
    modeKey: "products-items-page-selling-mode-unit-based",
    modeFallback: "Unit based",
    howKey: "products-items-page-selling-mode-unit-based-how",
    howFallback:
      "Sold by countable units, such as pieces, bottles, boxes, screws, or packets.",
    baseUnitKey: "products-items-page-selling-mode-unit-based-base-unit",
    baseUnitFallback: "Unit",
    partialKey: "products-items-page-selling-mode-unit-based-partial",
    partialFallback:
      "Optional. You can allow customers to buy part of the available quantity, or require the full quantity to be sold together."
  },
  {
    id: "measured",
    modeKey: "products-items-page-selling-mode-measured",
    modeFallback: "Measured",
    howKey: "products-items-page-selling-mode-measured-how",
    howFallback:
      "Sold by measurement, such as weight, volume, length, or area. Fractional quantities are supported.",
    baseUnitKey: "products-items-page-selling-mode-measured-base-unit",
    baseUnitFallback:
      "You choose the unit, such as kg, L, m, cm, or another measured unit.",
    partialKey: "products-items-page-selling-mode-measured-partial",
    partialFallback:
      "Optional. This is commonly enabled so customers can buy the exact amount they need."
  }
];
